import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Package, CheckCircle, AlertTriangle, Clock, Users, Truck, Loader2,
  BarChart3, Weight, CalendarCheck, MapPin, TrendingUp
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat('fr-FR').format(n || 0);
const fmtW = (n) => n >= 1000 ? `${(n / 1000).toFixed(1)}K` : fmt(n);

const KpiCard = ({ icon: Icon, iconColor, bgColor, label, value, sub, trend, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div className={`w-9 h-9 rounded-lg ${bgColor} flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${iconColor}`} />
        </div>
        {trend !== undefined && trend !== null && (
          <span className={`text-[11px] font-medium ${trend >= 80 ? 'text-emerald-600' : trend >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
            {trend}%
          </span>
        )}
      </div>
      <p className="text-2xl font-bold text-gray-900 mt-2">{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
      {sub && <p className="text-[10px] text-gray-400 mt-0.5">{sub}</p>}
    </CardContent>
  </Card>
);

const MONTH_LABELS = {
  '01': 'Jan', '02': 'Fev', '03': 'Mar', '04': 'Avr', '05': 'Mai', '06': 'Jun',
  '07': 'Jul', '08': 'Aou', '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dec'
};

const PartnerPerformance = ({ partnerId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');

  useEffect(() => {
    if (!partnerId) return;
    const fetchPerf = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/transport-partners/${partnerId}/performance?period=${period}`, {});
        setData(res.data);
      } catch { /* ignore */ }
      setLoading(false);
    };
    fetchPerf();
  }, [partnerId, period]);

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>;
  if (!data) return <div className="text-center py-12 text-gray-400 text-sm">Impossible de charger les performances</div>;

  const k = data.kpis;
  const p = data.presence;
  const monthly = (data.monthly || []).map(m => ({
    ...m,
    label: MONTH_LABELS[m.month?.split('-')[1]] || m.month,
  }));

  return (
    <div className="space-y-5" data-testid="partner-performance">
      {/* Period filter */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-gray-400" />
          Indicateurs de performance
        </h3>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-36 h-8 text-xs" data-testid="perf-period-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="30d">30 derniers jours</SelectItem>
            <SelectItem value="90d">90 derniers jours</SelectItem>
            <SelectItem value="6m">6 derniers mois</SelectItem>
            <SelectItem value="1y">1 an</SelectItem>
            <SelectItem value="all">Tout</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Operations breakdown */}
      <div>
        <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wider">Opérations</p>
        <div className="grid grid-cols-3 gap-3">
          <KpiCard icon={Package} iconColor="text-blue-600" bgColor="bg-blue-100"
            label="Total (Spot + Transferts)" value={fmt(k.totalOperations)}
            sub={`${k.active} en cours`}
            testId="kpi-total-ops" />
          <KpiCard icon={TrendingUp} iconColor="text-indigo-600" bgColor="bg-indigo-100"
            label="Operations Spot" value={fmt(k.totalSpot)}
            testId="kpi-spot-ops" />
          <KpiCard icon={Truck} iconColor="text-cyan-600" bgColor="bg-cyan-100"
            label="Transferts (voyages)" value={fmt(k.totalTransfers)}
            testId="kpi-transfer-ops" />
        </div>
      </div>

      {/* Performance KPIs */}
      <div>
        <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wider">Performance</p>
        <div className="grid grid-cols-2 gap-3">
          <KpiCard icon={CheckCircle} iconColor="text-emerald-600" bgColor="bg-emerald-100"
            label="Taux de realisation" value={`${k.completionRate}%`}
            sub={`${fmt(k.completed)} terminees · ${fmt(k.cancelled)} annulees`}
            trend={k.completionRate}
            testId="kpi-completion" />
          <KpiCard icon={Clock} iconColor="text-violet-600" bgColor="bg-violet-100"
            label="Ponctualite" value={k.punctualityRate !== null ? `${k.punctualityRate}%` : 'N/A'}
            sub={k.punctualityRate !== null ? `${k.onTime} a l'heure · ${k.late} en retard` : 'Pas assez de donnees'}
            trend={k.punctualityRate}
            testId="kpi-punctuality" />
        </div>
      </div>

      {/* Tonnage */}
      <div>
        <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wider">Tonnage</p>
        <div className="grid grid-cols-2 gap-3">
          <KpiCard icon={Weight} iconColor="text-amber-600" bgColor="bg-amber-100"
            label="Poids total transporte" value={`${fmtW(k.totalWeight)} T`}
            testId="kpi-total-weight" />
          <KpiCard icon={Weight} iconColor="text-orange-600" bgColor="bg-orange-100"
            label="Poids moyen / voyage" value={`${k.avgWeightPerTrip} T`}
            testId="kpi-avg-weight" />
        </div>
      </div>

      {/* Presence */}
      <div>
        <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wider">Presence (mois en cours)</p>
        <div className="grid grid-cols-3 gap-3">
          <KpiCard icon={CalendarCheck} iconColor="text-emerald-600" bgColor="bg-emerald-100"
            label="Jours actifs" value={p.activeDaysThisMonth}
            sub={`sur ${p.workingDaysInMonth} jours ouvrables`}
            testId="kpi-active-days" />
          <KpiCard icon={CalendarCheck} iconColor="text-red-500" bgColor="bg-red-50"
            label="Jours sans activite" value={p.absentDaysThisMonth}
            testId="kpi-absent-days" />
          <KpiCard icon={CalendarCheck} iconColor="text-blue-600" bgColor="bg-blue-100"
            label="Taux de presence" value={`${p.presenceRate}%`}
            trend={p.presenceRate}
            testId="kpi-presence-rate" />
        </div>
      </div>

      {/* Incidents */}
      <div>
        <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wider">Incidents</p>
        <div className="grid grid-cols-2 gap-3">
          <KpiCard icon={AlertTriangle} iconColor="text-red-600" bgColor="bg-red-100"
            label="Nb incidents" value={k.incidentsTotal}
            testId="kpi-incidents" />
          <KpiCard icon={AlertTriangle} iconColor="text-red-500" bgColor="bg-red-50"
            label="Taux d'incidents" value={`${k.incidentRate}%`}
            sub={`${k.incidentsTotal} / ${k.totalOperations} operations`}
            testId="kpi-incident-rate" />
        </div>
      </div>

      {/* Fleet summary */}
      <div className="flex items-center gap-3 text-xs bg-gray-50 rounded-lg p-3">
        <span className="flex items-center gap-1.5 text-gray-600">
          <Truck className="w-3.5 h-3.5 text-blue-500" />
          <strong>{data.fleet.vehicleCount}</strong> vehicule{data.fleet.vehicleCount > 1 ? 's' : ''}
        </span>
        <span className="text-gray-300">|</span>
        <span className="flex items-center gap-1.5 text-gray-600">
          <Users className="w-3.5 h-3.5 text-emerald-500" />
          <strong>{data.fleet.driverCount}</strong> chauffeur{data.fleet.driverCount > 1 ? 's' : ''}
        </span>
        <span className="text-gray-300">|</span>
        <span className="flex items-center gap-1.5 text-gray-600">
          <CalendarCheck className="w-3.5 h-3.5 text-violet-500" />
          <strong>{p.totalActiveDays}</strong> jours d'activite (total)
        </span>
      </div>

      {/* Top Routes */}
      {(data.topRoutes || []).length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <MapPin className="w-4 h-4 text-gray-400" />
              Top trajets
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5">
            {data.topRoutes.map((r, i) => (
              <div key={i} className="flex items-center justify-between text-xs py-1.5 border-b border-gray-100 last:border-0">
                <span className="text-gray-700 truncate max-w-[80%]">{r.route}</span>
                <span className="font-semibold text-gray-900 ml-2 shrink-0">{r.count}x</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Monthly chart - Operations + Tonnage */}
      {monthly.length > 0 && (
        <div className="grid grid-cols-1 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Evolution mensuelle - Opérations</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={monthly} margin={{ left: -10 }}>
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ fontSize: 11 }} />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="spot" name="Spot" fill="#6366f1" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="transfers" name="Transferts" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="cancelled" name="Annulees" fill="#ef4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Evolution mensuelle - Tonnage (T)</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={monthly} margin={{ left: -10 }}>
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ fontSize: 11 }} formatter={(v) => [`${v} T`, 'Tonnage']} />
                  <Bar dataKey="weight" name="Tonnage" fill="#1db584" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Empty state */}
      {k.totalOperations === 0 && (
        <div className="text-center py-8 text-gray-400">
          <Package className="w-8 h-8 mx-auto mb-2" />
          <p className="text-sm font-medium">Aucune opération</p>
          <p className="text-xs">Ce partenaire n'a pas encore d'operations enregistrees pour cette periode.</p>
        </div>
      )}
    </div>
  );
};

export default PartnerPerformance;
